/**
 * useUnreadCounts — polls /api/notifications/counts every 60 seconds.
 *
 * Returns { messageUnread, formSubmissionNew } for sidebar badge display.
 * Pauses polling when the browser tab is not visible (document.hidden).
 *
 * Usage:
 *   const { messageUnread, formSubmissionNew } = useUnreadCounts()
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { notifications as notificationsApi } from '../lib/api.js';

export interface UnreadCounts {
  messageUnread:     number;
  formSubmissionNew: number;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds

export function useUnreadCounts(): UnreadCounts {
  const { isStaff } = useAuth();
  const [counts, setCounts] = useState<UnreadCounts>({ messageUnread: 0, formSubmissionNew: 0 });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!isStaff || document.hidden) return;
    try {
      const res = await notificationsApi.counts();
      if (res.success && res.data) {
        setCounts({
          messageUnread:     res.data.messageUnread,
          formSubmissionNew: res.data.formSubmissionNew,
        });
      }
    } catch {
      // Non-fatal — badges just won't update until next poll
    }
  }, [isStaff]);

  useEffect(() => {
    if (!isStaff) return;

    // Initial fetch
    void fetchCounts();

    // Set up polling loop
    const schedule = (): void => {
      timerRef.current = setTimeout(() => {
        void fetchCounts();
        schedule();
      }, POLL_INTERVAL_MS);
    };
    schedule();

    // Refresh immediately when tab becomes visible again
    const handleVisibility = (): void => {
      if (!document.hidden) void fetchCounts();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isStaff, fetchCounts]);

  return counts;
}
